import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, Check, Clock,
  User, Calendar, Signature
} from 'lucide-react';
import { toast } from 'sonner';

export default function ContractManagement() {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [booking, setBooking] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('default_dj');
  const [customTerms, setCustomTerms] = useState('');
  const [generating, setGenerating] = useState(false);
  const [signing, setSigning] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch booking details
      const bookingRes = await apiClient.get(`/bookings/${bookingId}`);
      setBooking(bookingRes.data);
      
      // Fetch existing contract if any
      try {
        const contractRes = await apiClient.get(`/bookings/${bookingId}/contract`);
        setContract(contractRes.data.contract);
      } catch (e) {
        // No contract yet
      }
      
      // Fetch templates
      const templatesRes = await apiClient.get('/contracts/templates');
      setTemplates(templatesRes.data);
      
    } catch (error) {
      toast.error('Failed to load booking details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateContract = async () => {
    setGenerating(true);
    try {
      await apiClient.post(`/bookings/${bookingId}/contract`, {
        template_id: selectedTemplate,
        custom_terms: customTerms
      });
      toast.success('Contract generated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate contract');
    } finally {
      setGenerating(false);
    }
  };

  const signContract = async () => {
    if (!window.confirm('By signing, you agree to all terms in this contract. Continue?')) return;
    
    setSigning(true);
    try {
      await apiClient.post(`/contracts/${contract.contract_id}/sign`, {
        signature: user.name || user.email
      });
      toast.success('Contract signed successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      signed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4" data-testid="contract-management-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-8 h-8 text-violet-400" />
          <h1 className="text-3xl font-bold text-white">Contract Management</h1>
        </div>
        <p className="text-white/60 mb-8">Generate and sign contracts for your bookings</p>

        {/* Booking Summary */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-400" />
              Booking Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-white/40 text-sm">Event Date</p>
                <p className="text-white font-medium">
                  {booking?.event_date ? new Date(booking.event_date).toLocaleDateString() : 'TBD'}
                </p>
              </div>
              <div>
                <p className="text-white/40 text-sm">Venue</p>
                <p className="text-white font-medium">{booking?.venue || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-white/40 text-sm">Event Type</p>
                <p className="text-white font-medium">{booking?.event_type || 'Event'}</p>
              </div>
              <div>
                <p className="text-white/40 text-sm">Total Amount</p>
                <p className="text-green-400 font-medium">${booking?.total_cost?.toLocaleString() || '0'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contract Status or Generation */}
        {contract ? (
          <>
            {/* Existing Contract */}
            <Card className="bg-white/5 border-white/10 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-violet-400" />
                    Contract #{contract.contract_id?.slice(-8)}
                  </CardTitle>
                  {getStatusBadge(contract.status)}
                </div>
              </CardHeader>
              <CardContent>
                {/* Contract Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <h3 className="text-white font-medium border-b border-white/10 pb-2">Artist</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{contract.artist_name}</p>
                        <p className="text-white/40 text-sm">
                          {contract.artist_signed ? (
                            <span className="text-green-400 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Signed
                            </span>
                          ) : (
                            <span className="text-yellow-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Pending signature
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-white font-medium border-b border-white/10 pb-2">Client</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{contract.client_name}</p>
                        <p className="text-white/40 text-sm">
                          {contract.client_signed ? (
                            <span className="text-green-400 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Signed
                            </span>
                          ) : (
                            <span className="text-yellow-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Pending signature
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contract Terms */}
                {contract.terms && (
                  <div className="mb-6">
                    <h3 className="text-white font-medium mb-2">Custom Terms</h3>
                    <p className="text-white/60 bg-white/5 rounded-lg p-4 whitespace-pre-wrap">
                      {contract.terms}
                    </p>
                  </div>
                )}

                {/* Sign Button */}
                {contract.status !== 'signed' && (
                  <div className="flex gap-3">
                    {(user?.user_id === contract.artist_id || user?.user_id === contract.client_id) && (
                      <Button 
                        onClick={signContract} 
                        disabled={signing}
                        className="bg-violet-600 hover:bg-violet-500"
                      >
                        <Signature className="w-4 h-4 mr-2" />
                        {signing ? 'Signing...' : 'Sign Contract'}
                      </Button>
                    )}
                  </div>
                )}

                {/* Fully Signed */}
                {contract.status === 'signed' && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
                    <Check className="w-6 h-6 text-green-400" />
                    <div>
                      <p className="text-green-400 font-medium">Contract Fully Signed</p>
                      <p className="text-white/60 text-sm">Both parties have agreed to the terms</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Generate New Contract */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-400" />
                  Generate Contract
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Template Selection */}
                <div>
                  <Label className="text-white/80">Contract Template</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {templates.map((template) => (
                      <button
                        key={template.template_id}
                        onClick={() => setSelectedTemplate(template.template_id)}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          selectedTemplate === template.template_id
                            ? 'bg-violet-500/20 border-violet-500'
                            : 'bg-white/5 border-white/10 hover:border-white/20'
                        }`}
                      >
                        <p className="text-white font-medium">{template.name}</p>
                        <p className="text-white/40 text-sm mt-1">{template.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Terms */}
                <div>
                  <Label className="text-white/80">Additional Terms (Optional)</Label>
                  <textarea
                    value={customTerms}
                    onChange={(e) => setCustomTerms(e.target.value)}
                    placeholder="Add any custom terms or special requirements..."
                    rows={4}
                    className="w-full mt-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:border-violet-500 outline-none resize-none"
                  />
                </div>

                <Button 
                  onClick={generateContract} 
                  disabled={generating}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {generating ? 'Generating...' : 'Generate Contract'}
                </Button>

                <p className="text-white/40 text-sm text-center">
                  The contract will be sent to both parties for review and signature
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {/* Back Button */}
        <div className="mt-6">
          <Button variant="outline" onClick={() => navigate(-1)}>
            ← Back to Booking
          </Button>
        </div>
      </div>
    </div>
  );
}
